import { useState } from 'react';
import { motion } from 'framer-motion';

const testimonials = [
  {
    id: 1,
    name: 'Florence Salmeron',
    avatar: 'F',
    color: 'from-[#3AAFA9] to-[#2D8B7A]',
    rating: 5,
    text: "Dr. Lisa Velasco is simply the best. I am extremely happy with my dentist and her practice. She truly cares about her patients and it shows in every visit.",
    procedure: 'Family Dental Care',
    date: 'February 2025',
  },
  {
    id: 2,
    name: 'Ofie Baal',
    avatar: 'O',
    color: 'from-[#E8956A] to-[#D4754B]',
    rating: 5,
    text: "She is a great dentist. Always pleasant and calm. She's been our dentist for a long, long time. We love her and her staff.",
    procedure: 'General Dentistry',
    date: 'November 2024',
  },
  {
    id: 3,
    name: 'Rachel Guanlao',
    avatar: 'R',
    color: 'from-[#D4A853] to-[#B8903C]',
    rating: 5,
    text: "These dentists are amazing! They are super friendly, understanding, and when they work on your teeth they are very gentle. Their gentleness was new to me compared to my previous dentist.",
    procedure: 'Gentle Dental Care',
    date: '2021',
  },
];

export default function Testimonials() {
  const [activeIdx, setActiveIdx] = useState(0);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  return (
    <section id="testimonials" className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-primary)]/[0.03] via-transparent to-[var(--color-accent)]/[0.03]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--color-gold)]/10 text-[var(--color-gold)] text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Patient Stories
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'var(--font-display)' }}>
            What Our <span className="gradient-text">Patients</span> Say
          </h2>
          <p className="text-lg text-[var(--color-text-light)] leading-relaxed">
            Real stories from real patients who have experienced the exceptional care at our practice.
          </p>
        </motion.div>

        {/* Featured Photo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-14"
        >
          <div className="relative max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-2xl shadow-[var(--color-primary)]/10">
            <img
              src="/testimonials-photo.png"
              alt="Happy patient at Dr. Lisa Holly Abis-Velasco's dental practice"
              className="w-full h-[320px] sm:h-[400px] lg:h-[450px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-navy)]/40 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <p className="text-white/90 text-lg font-medium" style={{ fontFamily: 'var(--font-display)' }}>
                "Every smile tells a story — we're honored to be part of yours."
              </p>
              <p className="text-white/60 text-sm mt-1">— Dr. Lisa Holly Abis-Velasco</p>
            </div>
          </div>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16 max-w-3xl mx-auto"
        >
          {[
            { value: '4.9', label: 'Average Rating', icon: '⭐' },
            { value: '2,500+', label: 'Happy Patients', icon: '😊' },
            { value: '15+', label: 'Years Experience', icon: '🏥' },
            { value: '98%', label: 'Satisfaction Rate', icon: '💯' },
          ].map((stat, i) => (
            <div key={i} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-sm hover:shadow-md transition-shadow">
              <span className="text-xl mb-1 block">{stat.icon}</span>
              <p className="text-2xl font-bold text-[var(--color-navy)]" style={{ fontFamily: 'var(--font-display)' }}>{stat.value}</p>
              <p className="text-xs text-[var(--color-text-light)] mt-0.5">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Testimonial grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, i) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              onMouseEnter={() => setHoveredCard(testimonial.id)}
              onMouseLeave={() => setHoveredCard(null)}
              className={`relative bg-white rounded-3xl p-7 shadow-sm transition-all duration-500 cursor-default ${
                hoveredCard === testimonial.id
                  ? 'shadow-2xl shadow-[var(--color-primary)]/10 scale-[1.02] border-2 border-[var(--color-primary)]/15'
                  : 'shadow-md hover:shadow-lg border-2 border-transparent'
              }`}
            >
              {/* Quote mark */}
              <div className="absolute top-6 right-6 text-6xl font-serif text-[var(--color-primary)]/10 leading-none select-none" style={{ fontFamily: 'var(--font-display)' }}>
                "
              </div>

              {/* Rating */}
              <div className="flex gap-0.5 mb-4">
                {[1,2,3,4,5].map(s => (
                  <svg key={s} className="w-4 h-4 text-[var(--color-gold)]" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>

              {/* Text */}
              <p className="text-[var(--color-text)] leading-relaxed mb-6 text-sm relative z-10">
                "{testimonial.text}"
              </p>

              {/* Procedure tag */}
              <div className="mb-4">
                <span className="inline-block px-3 py-1 rounded-full bg-[var(--color-primary)]/8 text-[var(--color-primary)] text-xs font-medium">
                  {testimonial.procedure}
                </span>
              </div>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${testimonial.color} flex items-center justify-center text-white font-bold text-sm shadow-md`}>
                  {testimonial.avatar}
                </div>
                <div>
                  <p className="font-semibold text-sm text-[var(--color-navy)]">{testimonial.name}</p>
                  <p className="text-xs text-[var(--color-text-light)]">{testimonial.date}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
